import type {
  QueryClient,
  UseQueryOptions} from '@tanstack/react-query';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import type { UndefinedInitialDataOptions } from '@tanstack/react-query/src/queryOptions';

import type {
  SignedMessage} from '@api';
import {
  depositLaunchpadFcfs,
  depositLaunchpadProRata,
  depositLaunchpadProRataSqrpGated,
  depositLaunchpadSqrpGated,
  getFcfsAllocationsByPoolId,
  getLaunchpadProjects,
  getLaunchpadProjectWithSales,
  getLaunchpadSale,
  getLaunchpadSales,
  getProRataAllocationsByPoolId,
  getProRataSqrpGatedAllocationsByPoolId,
  getSelfLaunchpadProjectWithSales,
  getSqrpGatedAllocationsByPoolId
} from '@api';

import { launchpadQueryKeys } from '@shared/hooks/query/launchpad';
import { userQueryKeys } from '@shared/hooks/query/user';

import { AUTH_USER_QUERY_KEY } from './keys';

export const DEFAULT_LIMIT_LAUNCHPAD_PROJECTS = 4;

export const launchpadProjectKey = {
  list: (params: Required<Parameters<typeof getLaunchpadProjects>>[0]['searchParams']) => [
    'listLaunchpadProjects',
    params,
  ],
  infiniteList: (limit = 0, offset = 0, status = '') => [
    'infiniteListLaunchpadProject',
    limit,
    offset,
    status,
  ],
  project: (id: string, isAuth: boolean) => [
    'launchpadProject',
    id,
    isAuth ? AUTH_USER_QUERY_KEY : '',
  ],
  projectPrefetch: (id: string) => ['prefetchLaunchpadProject', id],
  sales: (id: string) => ['launchpadSales', id],
  sale: (id: string) => ['launchpadSale', id],
};

export const useLaunchpadProjectsListInfiniteQuery = (
  params: Parameters<typeof getLaunchpadProjects>[0],
) => {
  return useInfiniteQuery({
    queryKey: launchpadProjectKey.infiniteList(
      params?.searchParams?.limit || DEFAULT_LIMIT_LAUNCHPAD_PROJECTS,
      params?.searchParams?.offset || 0,
      params?.searchParams?.status,
    ),
    queryFn: ({ pageParam: offset }) =>
      getLaunchpadProjects({
        searchParams: {
          limit: params?.searchParams?.limit || DEFAULT_LIMIT_LAUNCHPAD_PROJECTS,
          offset,
          ...(params?.searchParams?.status ? { status: params?.searchParams?.status } : {}),
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages = []) =>
      lastPage?.length === DEFAULT_LIMIT_LAUNCHPAD_PROJECTS
        ? pages.length * DEFAULT_LIMIT_LAUNCHPAD_PROJECTS
        : undefined,
    staleTime: 0,
  });
};

export const useLaunchpadProjectsListQuery = (
  params?: Parameters<typeof getLaunchpadProjects>[0],
  options?: Pick<
    UndefinedInitialDataOptions<
      Awaited<ReturnType<typeof getLaunchpadProjects>>,
      unknown,
      unknown,
      any
    >,
    'placeholderData'
  >,
) => {
  return useQuery({
    queryKey: launchpadProjectKey.list(params?.searchParams),
    queryFn: () => getLaunchpadProjects(params),
    staleTime: 0,
    ...options,
  });
};

export const useLaunchpadProjectWithSalesQuery = (id: string, isAuth = false) => {
  return useQuery({
    queryKey: launchpadProjectKey.project(id, isAuth),
    queryFn: () =>
      isAuth ? getSelfLaunchpadProjectWithSales(id) : getLaunchpadProjectWithSales(id),
    staleTime: 0,
  });
};

export const useLaunchpadProjectWithSalesPrefetchQuery = (id: string) => {
  return useQuery({
    queryKey: launchpadProjectKey.projectPrefetch(id),
    queryFn: () => getLaunchpadProjectWithSales(id),
  });
};

export const prefetchLaunchpadProjectWithSalesQuery = (clientQuery: QueryClient, id: string) => {
  return clientQuery.prefetchQuery({
    queryKey: launchpadProjectKey.projectPrefetch(id),
    queryFn: () => getLaunchpadProjectWithSales(id),
  });
};

export const useLaunchpadSaleQuery = (id: string) => {
  return useQuery({
    queryKey: launchpadProjectKey.sale(id),
    queryFn: () => getLaunchpadSale(id),
    staleTime: 0,
  });
};

export const useLaunchpadSalesQuery = (id: string) => {
  return useQuery({
    queryKey: launchpadProjectKey.sales(id),
    queryFn: () => getLaunchpadSales(id),
    staleTime: 0,
  });
};

export const useLaunchpadDepositFcfsMutation = () => {
  return useMutation({
    mutationFn: ({
      saleId,
      amount,
      isSqrpBasedOffchainTransactions,
      signedMessage,
    }: {
      saleId: string;
      amount: number;
      isSqrpBasedOffchainTransactions: boolean;
      signedMessage?: SignedMessage;
    }) => depositLaunchpadFcfs(saleId, amount, isSqrpBasedOffchainTransactions, signedMessage),
  });
};

export const useGetFcfsAllocationsByPoolIdQuery = (
  saleId: string,
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof getFcfsAllocationsByPoolId>>>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    ...options,
    queryKey: [launchpadQueryKeys.fcfsAllocationsBySalePoolId(saleId)],
    queryFn: () => getFcfsAllocationsByPoolId(saleId),
  });
};

export const useLaunchpadDepositProRataMutation = () => {
  return useMutation({
    mutationFn: ({
      saleId,
      amount,
      boosted,
      isSqrpBasedOffchainTransactions,
      signedMessage,
    }: {
      saleId: string;
      amount: number;
      boosted: boolean;
      isSqrpBasedOffchainTransactions: boolean;
      signedMessage?: SignedMessage;
    }) =>
      depositLaunchpadProRata(
        saleId,
        amount,
        boosted,
        isSqrpBasedOffchainTransactions,
        signedMessage,
      ),
  });
};

export const useGetProRataAllocationsByPoolIdQuery = (
  saleId: string,
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof getProRataAllocationsByPoolId>>>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    ...options,
    queryKey: [launchpadQueryKeys.proRataAllocationsBySalePoolId(saleId)],
    queryFn: () => getProRataAllocationsByPoolId(saleId),
  });
};

export const useLaunchpadDepositProRataSqrpGatedMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      saleId,
      amount,
      boosted,
      isSqrpBasedOffchainTransactions,
      signedMessage,
    }: {
      saleId: string;
      amount: number;
      boosted: boolean;
      isSqrpBasedOffchainTransactions: boolean;
      signedMessage?: SignedMessage;
    }) => {
      return depositLaunchpadProRataSqrpGated(
        saleId,
        amount,
        boosted,
        isSqrpBasedOffchainTransactions,
        signedMessage,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [userQueryKeys.session] });
    },
  });
};

export const useGetProRataSqrpGatedAllocationsByPoolIdQuery = (
  saleId: string,
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof getProRataSqrpGatedAllocationsByPoolId>>>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    ...options,
    queryKey: [launchpadQueryKeys.proRataSqrpGatedAllocationsBySalePoolId(saleId)],
    queryFn: () => getProRataSqrpGatedAllocationsByPoolId(saleId),
  });
};

export const useLaunchpadDepositSqrpGatedMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      saleId,
      amount,
      isSqrpBasedOffchainTransactions,
      signedMessage,
    }: {
      saleId: string;
      amount: number;
      isSqrpBasedOffchainTransactions: boolean;
      signedMessage?: SignedMessage;
    }) => {
      return depositLaunchpadSqrpGated(
        saleId,
        amount,
        isSqrpBasedOffchainTransactions,
        signedMessage,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [launchpadQueryKeys.getLaunchpadCurrentUser] });
    },
  });
};

export const useGetSqrpGatedAllocationsByPoolIdQuery = (
  saleId: string,
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof getSqrpGatedAllocationsByPoolId>>>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    ...options,
    queryKey: [launchpadQueryKeys.sqrpGatedAllocationsBySalePoolId(saleId)],
    queryFn: () => getSqrpGatedAllocationsByPoolId(saleId),
  });
};
